import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from '../i18n';

/**
 * Compact language picker — dropdown anchored bottom-end.
 * Variants:
 *   - "default": pill with current language short code + chevron
 *   - "ghost": minimal text-only
 */
export default function LanguageSwitcher({ variant = 'default', className = '' }) {
  const { locale, setLocale, locales, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = locales.find(l => l.code === locale) || locales[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const base =
    variant === 'ghost'
      ? 'inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-slate-600 hover:text-slate-900 rounded-md hover:bg-slate-100 transition-colors'
      : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-full transition-colors';

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="language-switcher-btn"
        aria-label={t('language.change')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={base}
      >
        <Globe className="w-4 h-4 shrink-0" />
        <span className="font-semibold tracking-wide">{current.short}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 w-44 rounded-xl bg-white shadow-xl ring-1 ring-slate-200 border border-slate-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
        >
          {locales.map((l) => {
            const active = l.code === locale;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                onClick={() => { setLocale(l.code); setOpen(false); }}
                data-testid={`language-option-${l.code}`}
                className={`flex items-center justify-between w-full px-3 py-2.5 text-sm text-left transition-colors ${
                  active ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-xs font-bold tracking-wide w-7 text-slate-500">{l.short}</span>
                  <span>{l.label}</span>
                </span>
                {active && <Check className="w-4 h-4 text-blue-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
